import { API_CONFIG } from '@/lib/api/config';
import { apiClient } from '@/lib/api/client';
import { readSelectedWorkspaceId } from '@/lib/api/workspaceScopedEndpoints';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

const withWorkspace = (url: string, workspaceId?: string | null): string => {
  const ws = workspaceId ?? readSelectedWorkspaceId();
  if (!ws) return url;
  return `${url}${url.includes('?') ? '&' : '?'}workspace_id=${encodeURIComponent(ws)}`;
};

export interface YouTubeChannel {
  id: string;
  yt_channel_id: string;
  yt_channel_handle: string | null;
  yt_channel_title: string;
  yt_channel_description: string | null;
  yt_thumbnail_url: string | null;
  yt_country: string | null;
  yt_custom_url: string | null;
  subscriber_count: number;
  video_count: number;
  view_count: number;
  google_email?: string | null;
  is_active: boolean;
  token_expires_at: string | null;
  token_status?: string | null;
  last_synced_at: string | null;
  created_at: string;
}

export function isYouTubeTokenUnhealthy(
  channel: Pick<YouTubeChannel, 'token_status'> | null | undefined,
): boolean {
  return (channel?.token_status ?? '').trim().toUpperCase() === 'EXPIRED';
}

export interface YouTubeOAuthUrlResponse {
  authUrl: string;
  state: string;
  redirectUri: string;
}

export interface YouTubeConnectResult {
  id: string;
  yt_channel_id: string;
  yt_channel_title: string;
  yt_channel_handle: string | null;
  isNew: boolean;
}

export type YouTubeApiResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: string;
      code?: string;
      statusCode?: number;
      limit?: number;
      usage?: number;
      needsReconnect?: boolean;
    };

export const youtubeApi = {
  async getAuthUrl(origin?: string): Promise<YouTubeApiResult<YouTubeOAuthUrlResponse>> {
    const qs = origin ? `?origin=${encodeURIComponent(origin)}` : '';
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/oauth/url${qs}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || json?.success === false) {
      return {
        ok: false,
        error: json?.error || 'Failed to generate YouTube authorization URL',
        code: json?.code,
        statusCode: res.status,
        limit: json?.limit,
        usage: json?.usage,
      };
    }
    return { ok: true, data: json.data };
  },

  async exchangeCode(payload: { code: string; redirect_uri?: string; business_account_id?: string | null; workspace_id?: string | null }): Promise<YouTubeApiResult<YouTubeConnectResult>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/oauth/exchange`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || json?.success === false) {
      return {
        ok: false,
        error: json?.error || 'Failed to connect YouTube channel',
        code: json?.code,
        statusCode: res.status,
      };
    }
    return { ok: true, data: json.data };
  },

  /**
   * 🔴 ROUTED THROUGH `apiClient`, NOT A RAW `fetch` — deliberately unlike the
   * rest of this file.
   *
   * This one call decides whether the Social Posts page thinks a workspace has
   * ANY YouTube channel: its result feeds `youtubeChannels`, and an empty array
   * renders the full "Connect YouTube" empty state. A raw `fetch` carries no
   * token refresh and no 401 retry, so a merely-expired access token returned
   * `ok: false` here, the caller mapped that to `[]`, and a workspace with live
   * channels (verified in prod: 2 active channels, 17 videos) was told to
   * connect one. Instagram/Facebook/LinkedIn never hit this because their
   * account lists already go through `apiClient`.
   *
   * The other 29 raw fetches in this file are left alone on purpose — they back
   * individual actions that surface their own errors, not the page-level
   * "is anything connected at all?" gate.
   */
  async listChannels(businessAccountId?: string | null, workspaceId?: string | null): Promise<YouTubeApiResult<YouTubeChannel[]>> {
    const params: Record<string, string> = {};
    if (businessAccountId) params.business_account_id = businessAccountId;
    if (workspaceId) params.workspace_id = workspaceId;

    try {
      const res: any = await apiClient.get('/youtube/channels', { params });
      if (res?.error || res?.success === false) {
        return {
          ok: false,
          error: res?.error || 'Failed to load YouTube channels',
          code: res?.code,
        };
      }
      const data = Array.isArray(res?.data) ? (res.data as YouTubeChannel[]) : [];
      return { ok: true, data };
    } catch (err: any) {
      // A thrown ApiError still has to come back as `ok: false` — never as an
      // empty list, which the caller cannot tell apart from "no channels".
      return {
        ok: false,
        error: err?.error || err?.message || 'Failed to load YouTube channels',
        code: err?.code,
        statusCode: err?.statusCode,
      };
    }
  },

  async getChannel(channelId: string, workspaceId?: string | null): Promise<YouTubeApiResult<YouTubeChannel>> {
    const res = await fetch(withWorkspace(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}`, workspaceId), {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || json?.success === false) {
      return {
        ok: false,
        error: json?.error || 'Failed to load YouTube channel',
        code: json?.code,
        statusCode: res.status,
      };
    }
    return { ok: true, data: json.data };
  },

  async disconnectChannel(channelId: string, workspaceId?: string | null): Promise<YouTubeApiResult<{ message: string }>> {
    const res = await fetch(withWorkspace(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}`, workspaceId), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || json?.success === false) {
      return {
        ok: false,
        error: json?.error || 'Failed to disconnect channel',
        code: json?.code,
        statusCode: res.status,
      };
    }
    return { ok: true, data: { message: json?.message || 'Disconnected' } };
  },

  async refreshChannel(channelId: string, workspaceId?: string | null): Promise<YouTubeApiResult<{ message: string }>> {
    const res = await fetch(withWorkspace(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}/refresh`, workspaceId), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || json?.success === false) {
      return {
        ok: false,
        error: json?.error || 'Failed to refresh channel',
        code: json?.code,
        statusCode: res.status,
        needsReconnect: json?.needsReconnect === true,
      };
    }
    return { ok: true, data: { message: json?.message || 'Refreshed' } };
  },
};

export interface YouTubeVideo {
  id: string;
  organization_id: string;
  youtube_channel_id: string;
  yt_video_id: string | null;
  title: string;
  description: string | null;
  tags_json: string | null;
  category_id: string | null;
  privacy_status: 'private' | 'public' | 'unlisted' | string;
  made_for_kids: boolean;
  s3_key: string | null;
  s3_bucket: string | null;
  file_size_bytes: number | null;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  /**
   * The uploaded video, as a public URL — the same field Instagram and
   * Facebook store their media in.
   *
   * `s3_key` above is the private bucket path the publish worker streams from;
   * it is not fetchable by the browser. This is what the composer, card and
   * drawer can actually render.
   */
  media_urls: string[];
  publish_at: string | null;
  status: 'draft' | 'uploading' | 'processing' | 'published' | 'scheduled' | 'failed' | string;
  error_message: string | null;
  view_count: number;
  like_count: number;
  comment_count: number;
  published_at: string | null;
  last_synced_at: string | null;
  created_at: string;
}

export interface YouTubeVideoCategory { id: string; label: string; }

export interface YouTubeAiMetadata {
  title: string;
  description: string;
  tags: string[];
  categoryId: string;
}

export interface YouTubeComment {
  id: string;
  yt_comment_id: string;
  yt_video_id: string;
  parent_comment_id: string | null;
  author_display_name: string | null;
  author_profile_image_url: string | null;
  text_display: string | null;
  text_original: string | null;
  like_count: number;
  ai_intent: string | null;
  ai_replied: boolean;
  ai_reply_text: string | null;
  replied_at: string | null;
  yt_published_at: string | null;
  reply_count?: number;
  video?: { title: string | null; thumbnail_url: string | null } | null;
}

export interface YouTubeCommentSummary {
  total: number;
  unreplied: number;
  ai_replied: number;
  last_7d: number;
}

export interface YouTubeInsightsResponse {
  metadata: { period: string; start_date: string; end_date: string; channel_id: string; channel_title: string; };
  overview: {
    views: number;
    watch_time_minutes: number;
    avg_view_duration_seconds: number;
    subscribers_gained: number;
    subscribers_lost: number;
    net_subscribers: number;
    likes: number;
    comments: number;
  };
  top_videos: Array<{ yt_video_id: string; views: number; watch_time_minutes: number; likes: number; comments: number; }>;
  timeseries: Array<{ day: string; views: number; watch_time_minutes: number; subscribers_gained: number; likes: number; }>;
  geography?: Array<{ country: string; views: number; watch_time_minutes: number; avg_view_duration_seconds: number; }>;
  demographics?: Array<{ age_group: string; gender: string; viewer_percentage: number; }>;
  traffic_sources?: Array<{ source: string; views: number; watch_time_minutes: number; }>;
  device_split?: Array<{ device: string; views: number; watch_time_minutes: number; }>;
  channel_summary: { subscribers: number; video_count: number; lifetime_views: number; };
  analytics_error?: { status: number; reason: string } | null;
}

export interface YouTubeAutomation {
  id: string;
  organization_id: string;
  youtube_channel_id: string;
  mode: 'off' | 'reply' | 'classify_only';
  config_json: string | null;
  fallback_message: string | null;
  ai_paused_until: string | null;
  last_run_at: string | null;
}

const json = async (res: Response) => {
  try { return await res.json(); } catch { return {} as any; }
};

const YT_LIST_TTL_MS = 12_000;
const ytListCache = new Map<
  string,
  { at: number; promise: Promise<YouTubeApiResult<YouTubeVideo[]>> }
>();

export function invalidateYouTubeVideosListCache(channelId?: string): void {
  if (!channelId) {
    ytListCache.clear();
    return;
  }
  ytListCache.delete(channelId);
}

async function listYouTubeVideosUncached(
  channelId: string,
): Promise<YouTubeApiResult<YouTubeVideo[]>> {
  const res = await fetch(
    `${API_CONFIG.BASE_URL}/youtube/videos?channelId=${encodeURIComponent(channelId)}`,
    { headers: getAuthHeaders() },
  );
  const j = await json(res);
  if (!res.ok || j?.success === false) {
    return { ok: false, error: j?.error || 'Failed to load videos', statusCode: res.status };
  }
  return { ok: true, data: j.data || [] };
}

export const youtubeVideosApi = {
  async initUpload(payload: {
    channelId: string;
    fileName: string;
    fileSizeBytes: number;
    contentType?: string;
    metadata?: {
      title?: string;
      description?: string;
      tags?: string[];
      categoryId?: string;
      privacyStatus?: 'private' | 'public' | 'unlisted';
      madeForKids?: boolean;
      publishAt?: string;
      /**
       * A poster frame grabbed from the file in the browser, already uploaded.
       *
       * YouTube does not serve a thumbnail for a PRIVATE video, and an upload
       * defaults to private — so without one of our own the card is a grey box
       * until someone makes the video public. Every other composer captures
       * this for a video post already.
       */
      thumbnailUrl?: string;
    };
  }): Promise<YouTubeApiResult<{
    videoRowId: string;
    uploadUrl: string;
    s3Key: string;
    s3Bucket: string;
    maxBytes: number;
  }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/init`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to start upload', code: j?.code, statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async putToS3(uploadUrl: string, file: File, onProgress?: (pct: number) => void): Promise<{ ok: boolean; status: number; error?: string; }> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
        };
      }
      xhr.onload = () => resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        error: xhr.status >= 400 ? `S3 PUT ${xhr.status}` : undefined,
      });
      xhr.onerror = () => resolve({ ok: false, status: 0, error: 'Network error during S3 upload' });
      xhr.send(file);
    });
  },

  async finalize(videoRowId: string): Promise<YouTubeApiResult<{ status: string; alreadyFinalizing?: boolean; video: YouTubeVideo; }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/${videoRowId}/finalize`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to finalize upload', code: j?.code, statusCode: res.status };
    }
    invalidateYouTubeVideosListCache();
    return { ok: true, data: j.data };
  },

  async list(channelId: string): Promise<YouTubeApiResult<YouTubeVideo[]>> {
    const id = String(channelId || '').trim();
    if (!id) {
      return { ok: false, error: 'channelId is required', statusCode: 400 };
    }
    const cached = ytListCache.get(id);
    if (cached && Date.now() - cached.at < YT_LIST_TTL_MS) {
      return cached.promise;
    }
    const promise = listYouTubeVideosUncached(id).then((result) => {
      if (!result.ok) ytListCache.delete(id);
      return result;
    });
    ytListCache.set(id, { at: Date.now(), promise });
    return promise;
  },

  async get(videoId: string): Promise<YouTubeApiResult<YouTubeVideo>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/${videoId}`, { headers: getAuthHeaders() });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load video', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async syncFromChannel(channelId: string): Promise<YouTubeApiResult<{ inserted: number; updated: number; total: number }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/sync`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channelId }),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Sync failed', statusCode: res.status };
    }
    invalidateYouTubeVideosListCache(channelId);
    return { ok: true, data: { inserted: j.inserted ?? 0, updated: j.updated ?? 0, total: j.total ?? 0 } };
  },

  async refreshStats(channelId: string): Promise<YouTubeApiResult<{ refreshed: number }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/refresh-stats`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channelId }),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to refresh', statusCode: res.status };
    }
    invalidateYouTubeVideosListCache(channelId);
    return { ok: true, data: j.data };
  },

  async update(videoId: string, patch: Partial<{
    title: string; description: string; tags: string[]; categoryId: string;
    privacyStatus: 'private' | 'public' | 'unlisted'; madeForKids: boolean;
    publishAt: string | null; thumbnailUrl: string;
  }>): Promise<YouTubeApiResult<YouTubeVideo>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/${videoId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to update', statusCode: res.status };
    }
    invalidateYouTubeVideosListCache();
    return { ok: true, data: j.data };
  },

  async delete(videoId: string): Promise<YouTubeApiResult<{ message: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/${videoId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to delete', statusCode: res.status };
    }
    invalidateYouTubeVideosListCache();
    return { ok: true, data: { message: j?.message || 'Deleted' } };
  },

  async generateMetadata(payload: { brief: string; audience?: string; tone?: string; }): Promise<YouTubeApiResult<YouTubeAiMetadata>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/ai-metadata`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to generate metadata', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async getCategories(): Promise<YouTubeApiResult<YouTubeVideoCategory[]>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/videos/categories`, { headers: getAuthHeaders() });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load categories', statusCode: res.status };
    }
    return { ok: true, data: j.data || [] };
  },
};

export const youtubeCommentsApi = {
  async sync(channelId: string, maxVideos = 20): Promise<YouTubeApiResult<{ inserted: number; updated: number; videos: number; videoErrors: number; }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/comments/sync`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channelId, maxVideos }),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to sync comments', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async syncVideo(channelId: string, ytVideoId: string): Promise<YouTubeApiResult<{ inserted: number; updated: number; ytVideoId: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/comments/sync-video`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channelId, ytVideoId }),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to sync comments', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async list(
    channelId: string,
    opts: { unrepliedOnly?: boolean; ytVideoId?: string | null } = {},
  ): Promise<YouTubeApiResult<YouTubeComment[]>> {
    const params = new URLSearchParams({ channelId });
    if (opts.unrepliedOnly) params.set('unrepliedOnly', 'true');
    if (opts.ytVideoId) params.set('ytVideoId', opts.ytVideoId);
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/comments?${params.toString()}`, { headers: getAuthHeaders() });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load comments', statusCode: res.status };
    }
    return { ok: true, data: j.data || [] };
  },

  async getSummary(channelId: string): Promise<YouTubeApiResult<YouTubeCommentSummary>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/comments/summary?channelId=${encodeURIComponent(channelId)}`, {
      headers: getAuthHeaders(),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load summary', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async reply(commentId: string, channelId: string, text: string): Promise<YouTubeApiResult<{ replyId: string; parentId: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/comments/${commentId}/reply`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ channelId, text }),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to reply', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async getReplies(commentId: string, channelId: string): Promise<YouTubeApiResult<YouTubeComment[]>> {
    const params = new URLSearchParams({ channelId });
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/youtube/comments/${commentId}/replies?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load replies', statusCode: res.status };
    }
    return { ok: true, data: j.data || [] };
  },
};

export interface YouTubeCommunityPost {
  id: string;
  published_at: string | null;
  text: string;
  thumbnail_url: string | null;
  linked_video_id: string | null;
  post_type: 'text' | 'image' | 'video' | 'poll' | string;
  image_count: number;
}

export const youtubePostsApi = {
  async list(channelId: string): Promise<YouTubeApiResult<{ posts: YouTubeCommunityPost[]; total: number }>> {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/youtube/posts?channelId=${encodeURIComponent(channelId)}`,
      { headers: getAuthHeaders() },
    );
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load community posts', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async create(payload: {
    channelId: string;
    type: string;
    text: string;
    visibility: string;
    pollOptions?: string[];
    quizOptions?: Array<{ text: string; isCorrect: boolean }>;
    videoId?: string;
    imageUrls?: string[];
  }): Promise<YouTubeApiResult<{ id: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/community-posts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify(payload),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to create post', code: j?.code, statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async initImageUpload(payload: {
    channelId: string;
    fileName: string;
    contentType: string;
  }): Promise<YouTubeApiResult<{ uploadUrl: string; publicUrl: string; s3Key: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/community-post-images/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify(payload),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to get upload URL', code: j?.code, statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async putImageToS3(uploadUrl: string, file: File): Promise<{ ok: boolean; status: number; error?: string }> {
    try {
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      });
      return {
        ok: res.ok,
        status: res.status,
        error: res.ok ? undefined : `S3 PUT ${res.status}`,
      };
    } catch (e: any) {
      return { ok: false, status: 0, error: e?.message || 'Network error during S3 upload' };
    }
  },
};

export const youtubeInsightsApi = {
  async get(channelId: string, opts: { period?: string; startDate?: string; endDate?: string; } = {}): Promise<YouTubeApiResult<YouTubeInsightsResponse>> {
    const params = new URLSearchParams({ channelId });
    if (opts.period) params.set('period', opts.period);
    if (opts.startDate) params.set('startDate', opts.startDate);
    if (opts.endDate) params.set('endDate', opts.endDate);
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/insights?${params.toString()}`, { headers: getAuthHeaders() });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load insights', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },
};

export const youtubeAutomationApi = {
  async get(channelId: string): Promise<YouTubeApiResult<{ automation: YouTubeAutomation; summary: YouTubeCommentSummary }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}/automation`, { headers: getAuthHeaders() });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to load automation', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async update(channelId: string, patch: Partial<{
    mode: 'off' | 'reply' | 'classify_only';
    aiPausedUntil: string | null;
    fallbackMessage: string | null;
    configJson: any;
  }>): Promise<YouTubeApiResult<{ automation: YouTubeAutomation }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}/automation`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to update automation', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },

  async runNow(channelId: string): Promise<YouTubeApiResult<{ classified: number; replied: number; skipped_count: number; skipped?: string }>> {
    const res = await fetch(`${API_CONFIG.BASE_URL}/youtube/channels/${channelId}/automation/run`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const j = await json(res);
    if (!res.ok || j?.success === false) {
      return { ok: false, error: j?.error || 'Failed to run automation', statusCode: res.status };
    }
    return { ok: true, data: j.data };
  },
};
